/**
 * MicCheckPill — drop-in 3-second mic level test button.
 *
 * Wraps useMicCheck with the standard cyan pill UI used in the Suno
 * record-reference panel. While checking, the button morphs into a
 * live read-out showing peak dBFS + gradient meter bar + countdown.
 *
 * iter-162 — added to the DAW transport bar so users can verify their
 * mic anywhere in the app without having to dive into the Make Music modal.
 */
import { Volume2 } from "lucide-react";
import useMicCheck from "@/lib/useMicCheck";

export default function MicCheckPill({
  disabled = false,
  testId = "mic-check-pill",
  compact = false,
}) {
  const { checking, peak, durationMs, start } = useMicCheck();
  const peakDb = peak > 0 ? 20 * Math.log10(peak) : -Infinity;
  const peakDbStr = Number.isFinite(peakDb) ? peakDb.toFixed(1) : "−∞";
  const secsLeft = Math.max(0, Math.ceil((3000 - durationMs) / 1000));

  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled || checking}
      onClick={start}
      title={checking ? "Recording 3 s of audio to test your mic level" : "3-second mic level test — verify your input before laying down a take"}
      className={`inline-flex items-center gap-1.5 rounded-full border ${compact ? "px-2.5 py-1" : "px-3 py-1.5"} font-mono text-[10px] uppercase tracking-wider transition disabled:cursor-not-allowed disabled:opacity-50 ${
        checking
          ? "border-cyan-400/50 bg-cyan-500/[0.22] text-cyan-50 shadow-[0_0_14px_rgba(34,211,238,0.5)]"
          : "border-cyan-400/30 bg-cyan-500/[0.08] text-cyan-100 hover:bg-cyan-500/[0.18]"
      }`}
    >
      <Volume2 className={`h-3 w-3 ${checking ? "animate-pulse" : ""}`} />
      {checking ? (
        <>
          <span className="tabular-nums">{peakDbStr}</span>
          <span className="text-[8px] opacity-70">dB</span>
          <span className="ml-0.5 inline-block h-1.5 w-12 overflow-hidden rounded-full bg-black/40">
            <span
              className="block h-full bg-gradient-to-r from-emerald-400 via-amber-300 to-rose-500 transition-[width] duration-75"
              style={{ width: `${Math.min(100, peak * 100)}%` }}
            />
          </span>
          <span className="text-[8px] opacity-60">{secsLeft}s</span>
        </>
      ) : (
        "Mic check"
      )}
    </button>
  );
}
